import { useMemo } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

const renderMath = (tex, display) => {
  try {
    return katex.renderToString(tex, { throwOnError: false, displayMode: display, output: "html" });
  } catch {
    return tex;
  }
};

const escapeHtml = (s) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// A single whitespace-free token is math only if it carries real LaTeX markers.
// This prevents plain words (e.g. "Karena", "adalah") from being italicised/space-stripped by KaTeX.
const isMathToken = (t) => /[\\^_{}]/.test(t);

// Renders a delimiter-free chunk: split on whitespace, render only math-looking tokens as KaTeX,
// keep ordinary words as text and preserve the spaces between them.
function renderPlain(chunk) {
  if (!chunk) return "";
  const parts = chunk.split(/(\s+)/);
  let out = "";
  for (const p of parts) {
    if (p === "") continue;
    if (/^\s+$/.test(p)) { out += p; continue; }
    out += isMathToken(p) ? renderMath(p, false) : escapeHtml(p);
  }
  return out;
}

// Splits a string into text + delimited-math segments and renders math with KaTeX.
export default function MathText({ children, className }) {
  const html = useMemo(() => {
    const text = children == null ? "" : String(children);
    if (!text) return "";
    const re = /\\\((.+?)\\\)|\\\[(.+?)\\\]|\$(.+?)\$/gs;
    let out = "";
    let last = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      out += renderPlain(text.slice(last, m.index));
      const isDisplay = m[2] != null;
      out += renderMath(m[1] ?? m[2] ?? m[3] ?? "", isDisplay);
      last = re.lastIndex;
    }
    out += renderPlain(text.slice(last));
    return out;
  }, [children]);

  return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}
